import { useEffect, useState } from "react"
import cn from "classnames"
import { addDays, subDays } from "date-fns"

import { Button, Text, Modal, TextWithIcon, Img } from "ui-kit/components-branded/index"
import { OnlineOrOfflineTabs } from "modules/BookingCommon/components"
import { PriceAndTime, NotAvailableSessionsText } from "modules/Booking/SessionDatePage/components"
import { useBookingContext } from "modules/Booking/BookingContext"
import { Analytics } from "utils/analytics"
import { Spinner } from "components/Spinner"
import { SESSION_TYPE } from "modules/Booking/common/constants"
import { TimeRequestSelectors } from "modules/Booking/common/interfaces"
import { twoWeeks } from "modules/BookingCommon/common/utils"
import { getUtcTime, sortDateAndTime } from "modules/Booking/common/utils"
import { ServiceType } from "utils/helpers"
import { useService } from "modules/Booking/common/hooks"
import { CURRENCY } from "utils/constants"
import { MAX_COUNT_SELECTORS } from "modules/Booking/components/TimeRequestModal/common/constants"
import { Item } from "modules/Booking/components/TimeRequestModal/components/Item"
import {
    fetchHours,
    getPossibleHours,
} from "modules/Booking/components/TimeRequestModal/common/api"
import { AvailableLocalHours } from "modules/Booking/components/TimeRequestModal/common/interfaces"
import styles from "modules/Booking/components/TimeRequestModal/styles.module.scss"
import { getValidate } from "modules/Booking/components/TimeRequestModal/common/helpers"
import { useOnlyFreeSessions } from "pages/FreePsysPage/useOnlyFreeSessions"
import { format, formatToyLd } from "utils/datetime-format-utils"
import { HeadlineSessionDate } from "modules/BookingCommon/components/HeadlineSessionDate"

export function TimeRequestModal() {
    const [
        {
            onlineOrOffline,
            requestTimesSelectors,
            timeRequestModalActive,
            possibleHours,
            timeRequestsUtcTime,
            psy,
            currentWeek,
            selectedService,
            hasSlots,
            slotDuration,
        },
        {
            nextPage,
            setTimeRequestsSelectors,
            toggleTimeRequestModalActive,
            changeSessionType,
            setSlotId,
            changeSelectedTime,
            setSlotDuration,
            setPossibleHours,
            setTimeRequestsUtcTime,
            changeOnlineOrOffline,
            setIsChooseManuallyService,
            setCurrency,
        },
    ] = useBookingContext()
    const { onlineService, offlineService } = useService()

    const [validate, setValidate] = useState(true)
    const onlyFree = useOnlyFreeSessions()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const [daySlots, setDaySlots] = useState<AvailableLocalHours | undefined>(undefined)

    useEffect(() => {
        if (timeRequestModalActive) {
            setCurrency(CURRENCY.RUB)
            if (hasSlots) {
                Analytics.open_time_request_modal()
            } else {
                Analytics.open_time_request_no_slots_modal()
            }
        }
    }, [timeRequestModalActive])

    function fetchAvailableLocalHours() {
        if (currentWeek) {
            const twoWeeksData = twoWeeks(currentWeek)
            const from = twoWeeksData[0][0]
            fetchHours(
                from,
                14,
                psy.hash_id,
                onlyFree,
                (hours) => setDaySlots(hours),
                selectedService
            )
        }
    }

    useEffect(() => {
        if (currentWeek && timeRequestModalActive && selectedService) {
            setLoading(true)
            const twoWeeksData = twoWeeks(currentWeek)
            const from = twoWeeksData[0][0]
            const to = addDays(twoWeeksData[1][6], 1)
            const fromDate = subDays(from, 1)

            const options = {
                psy_hash_id: psy.hash_id,
                from_date: format(fromDate, "yyyy-MM-dd"),
                to_date: format(to, "yyyy-MM-dd"),
                offset_from_utc_minutes: -new Date().getTimezoneOffset(),
                service_type: new ServiceType(selectedService),
                for_psychologist: false,
                only_free: onlyFree,
            }

            getPossibleHours(options)
                .then((data) => {
                    setPossibleHours(data)
                    setTimeRequestsUtcTime(getUtcTime(data, fromDate, to))
                    fetchAvailableLocalHours()
                })
                .catch(() => {
                    setError(true)
                })
                .finally(() => {
                    setLoading(false)
                })
        }
    }, [currentWeek, timeRequestModalActive])

    function closeModal() {
        toggleTimeRequestModalActive(false)
        if (hasSlots) {
            Analytics.close_time_request_modal()
        } else {
            Analytics.close_time_request_no_slots_modal()
        }
    }

    function handleClickDate(value: string, index: number) {
        setTimeRequestsSelectors((prevState: TimeRequestSelectors[]) => {
            const newState = [...prevState]
            newState[index] = {
                date: value,
                time: undefined,
                id: undefined,
            }
            return newState
        })
    }

    function handleClickTime(time: string, index: number) {
        const date = new Date(`${requestTimesSelectors[index].date || ""} ${time}`)
        const timeZoneOffset = date.getTimezoneOffset() * 60 * 1000
        const nowDate = new Date(date.getTime() - timeZoneOffset)

        if (hasSlots) {
            Analytics.choose_time_time_request_modal({ time: nowDate })
        } else {
            Analytics.choose_time_time_request_no_slots_modal({ time: nowDate })
        }
        setTimeRequestsSelectors((prevState: TimeRequestSelectors[]) => {
            const newState = [...prevState]

            const dateWithUtc = new Date(date.getTime() + timeZoneOffset)

            const key = formatToyLd(dateWithUtc)
            if (possibleHours) {
                const findArray = possibleHours[key]
                if (findArray) {
                    const timeKey = format(dateWithUtc, "HH:mm")

                    const findTime = findArray.find((item) => item.start_time === timeKey)

                    if (findTime) {
                        newState[index] = {
                            ...newState[index],
                            time,
                            id: findTime.id,
                        }
                        return newState
                    }

                    newState[index] = {
                        ...newState[index],
                        time,
                        id: undefined,
                    }
                } else {
                    newState[index] = {
                        ...newState[index],
                        time,
                        id: undefined,
                    }
                }
            }

            return newState
        })
    }

    function handleClickAddButton() {
        setTimeRequestsSelectors((prevState: TimeRequestSelectors[]) => [
            ...prevState,
            {
                date: undefined,
                time: undefined,
                id: undefined,
            },
        ])
    }

    function handleClickButtonNext() {
        if (getValidate(requestTimesSelectors)) {
            const time = sortDateAndTime(requestTimesSelectors).find((item) => item.id !== null)

            if (time && time.id !== undefined) {
                const date = new Date(`${time.date || ""} ${time.time || ""}`)

                if (date) {
                    changeSessionType(SESSION_TYPE.DEFAULT)
                    setSlotId(time.id)
                    changeSelectedTime(date)
                    setSlotDuration(slotDuration)
                }
            } else {
                changeSessionType(SESSION_TYPE.TIME_REQUEST)
            }
            nextPage()
            toggleTimeRequestModalActive(false)
            if (hasSlots) {
                Analytics.user_went_to_form_after_time_request_modal()
            } else {
                Analytics.user_went_to_form_after_time_request_no_slots_modal()
            }
        } else {
            setValidate(false)
        }
    }
    return (
        <Modal isActive={timeRequestModalActive} onBackgroundClick={closeModal}>
            <Modal.Content
                className={styles.contentModal}
                isFullHeightMobile
                padding="M"
                data-testid="booking-time-request-modal"
                id="time-request-modal"
            >
                <div>
                    <HeadlineSessionDate className="mgb-4">
                        {onlineOrOffline === "offline" ? (
                            <>Выберите удобное время для личной сессии</>
                        ) : (
                            <>Выберите удобное время для онлайн-сессии</>
                        )}
                    </HeadlineSessionDate>
                    <OnlineOrOfflineTabs
                        onlineService={onlineService}
                        offlineService={offlineService}
                        onlineOrOffline={onlineOrOffline}
                        onChangeOnlineOrOffline={changeOnlineOrOffline}
                        onChangeChooseManuallyService={setIsChooseManuallyService}
                    />
                    <NotAvailableSessionsText />
                    <PriceAndTime className="mgb-2" />
                    {currentWeek && !loading ? (
                        <>
                            <Text
                                className={cn("is-block mgb-3", {
                                    [styles.descriptionIsDanger]: !validate,
                                })}
                            >
                                Выберите как минимум 3 варианта
                            </Text>
                            <div
                                className={cn(
                                    "date-time-dropdown",
                                    "is-flex",
                                    "is-flex-direction-column",
                                    {
                                        "mgb-2":
                                            getValidate(requestTimesSelectors) &&
                                            requestTimesSelectors.length < MAX_COUNT_SELECTORS,
                                    }
                                )}
                            >
                                {requestTimesSelectors.map((item, index) => (
                                    <Item
                                        items={requestTimesSelectors}
                                        item={item}
                                        index={index}
                                        utcTime={timeRequestsUtcTime}
                                        key={index}
                                        disabled={error}
                                        psyHashId={psy.hash_id}
                                        slots={daySlots}
                                        onClickDate={handleClickDate}
                                        onClickTime={handleClickTime}
                                    />
                                ))}
                            </div>
                            {getValidate(requestTimesSelectors) &&
                                requestTimesSelectors.length < MAX_COUNT_SELECTORS && (
                                    <div className={cn(styles.add, "is-block mgb-4")}>
                                        <Text
                                            isAccent
                                            onClick={handleClickAddButton}
                                            className={styles.addText}
                                        >
                                            Добавить еще
                                        </Text>
                                    </div>
                                )}
                        </>
                    ) : (
                        <Spinner />
                    )}
                    <div className={styles.bannerWrapper}>
                        <div className={cn(styles.banner, "p-3 mgt-3")}>
                            <TextWithIcon
                                icon={
                                    <Img
                                        className={styles.bannerEmoji}
                                        src="hourglass_flowing_sand.png"
                                    />
                                }
                                position="left"
                            >
                                Если психолог не&nbsp;подтвердит запись на&nbsp;выбранное время,
                                мы&nbsp;вернем вам деньги полностью.
                            </TextWithIcon>
                        </div>
                    </div>
                </div>
                {currentWeek && !loading && !error && (
                    <div className={styles.buttons}>
                        <Button className={cn(styles.button, styles.cancel)} onClick={closeModal}>
                            Отмена
                        </Button>
                        <Button
                            color="primary"
                            className={styles.button}
                            onClick={handleClickButtonNext}
                        >
                            Дальше
                        </Button>
                    </div>
                )}
                {error && (
                    <Text className={styles.error}>
                        Не&nbsp;удалось загрузить время. Попробуйте обновить страницу.
                    </Text>
                )}
            </Modal.Content>
        </Modal>
    )
}
